import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * A floating window that stays above other applications — for clinicians who
 * work in a different EMR and want Claro's scribe visible without hunting for
 * its tab (John, 2026-08-19).
 *
 * Two transports, deliberately:
 *
 *   * **Document Picture-in-Picture** where it exists (Chrome/Edge 116+,
 *     Firefox 151+) — a real always-on-top window. This is the one that earns
 *     the feature, because it floats over a NATIVE Windows EMR too, which no
 *     browser extension can do.
 *   * **`window.open`** everywhere else (Safari has never shipped document
 *     PiP). A genuine separate window that can be parked beside the EMR; what
 *     it loses is always-on-top. On two screens that costs nothing. On one it
 *     sinks behind the EMR — which is why `supported` is exposed, so callers
 *     can say so rather than silently degrading.
 *
 * The window is a SURFACE, not a home for state. Everything it shows is
 * rendered from `useRecordingStore` in the opener's JS context, so recording
 * survives the window being closed and `stores/recording.ts` needs no changes
 * at all. The one real consequence: a doc-PiP window dies with its opener tab,
 * so closing the Claro tab still ends the session exactly as it does today.
 */

interface DocumentPictureInPictureOptions {
  width?: number
  height?: number
  disallowReturnToOpener?: boolean
  preferInitialWindowPlacement?: boolean
}

interface DocumentPictureInPictureApi {
  requestWindow: (options?: DocumentPictureInPictureOptions) => Promise<Window>
  window: Window | null
}

declare global {
  interface Window {
    documentPictureInPicture?: DocumentPictureInPictureApi
  }
}

export interface PopoutSize {
  width: number
  height: number
}

export interface PopoutWindowApi {
  /** Portal target inside the pop-out, or null while closed. */
  container: HTMLElement | null
  isOpen: boolean
  /** True document Picture-in-Picture (always-on-top). False = popup fallback. */
  supported: boolean
  /** Requires a user gesture — call it straight from a click handler. */
  open: (size: PopoutSize) => Promise<void>
  close: () => void
  /** Resize the window. MUST be called from a user gesture — see `resize`. */
  resize: (size: PopoutSize) => void
}

/**
 * Give the pop-out the opener's styles.
 *
 * A fresh PiP document inherits NOTHING — no stylesheet, no font — so without
 * this the whole window renders as unstyled black Times on white. Same-origin
 * sheets are inlined from their rules (Vite injects the built CSS as a single
 * same-origin sheet in production and as `<style>` tags in dev, so this is the
 * path that actually runs); anything whose `cssRules` throws is cross-origin
 * and gets re-linked by href instead, which is the only thing that works for
 * a CDN or Google Fonts sheet.
 */
function copyStyles(source: Document, target: Document) {
  for (const sheet of Array.from(source.styleSheets)) {
    try {
      const rules = sheet.cssRules
      const style = target.createElement('style')
      style.textContent = Array.from(rules)
        .map((rule) => rule.cssText)
        .join('\n')
      target.head.appendChild(style)
    } catch {
      // Cross-origin sheet: the rules are unreadable, but the href is not.
      const href = sheet.href
      if (!href) continue
      const link = target.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      if (sheet.media.mediaText) link.media = sheet.media.mediaText
      target.head.appendChild(link)
    }
  }

  // Inline <style> tags Vite injects during dev are not always reachable as
  // readable stylesheets above; copying the elements themselves is cheap
  // insurance and duplicate rules are harmless.
  for (const node of Array.from(source.querySelectorAll('style'))) {
    target.head.appendChild(node.cloneNode(true))
  }
}

export function usePopoutWindow(): PopoutWindowApi {
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const windowRef = useRef<Window | null>(null)
  const supported = typeof window !== 'undefined' && !!window.documentPictureInPicture

  const teardown = useCallback(() => {
    windowRef.current = null
    setContainer(null)
  }, [])

  const close = useCallback(() => {
    const win = windowRef.current
    // Order matters: drop the React tree first so the portal is not still
    // pointing at a document that is being torn down.
    teardown()
    try {
      win?.close()
    } catch {
      // A window the browser already closed throws on some engines.
    }
  }, [teardown])

  const open = useCallback(
    async (size: PopoutSize) => {
      if (windowRef.current) return

      let win: Window | null = null
      if (window.documentPictureInPicture) {
        win = await window.documentPictureInPicture.requestWindow({
          width: size.width,
          height: size.height,
        })
      } else {
        win = window.open(
          '',
          'claro-scribe',
          `popup=yes,width=${size.width},height=${size.height}`,
        )
      }

      // A popup blocker returns null. Callers surface this; throwing here
      // would take down the click handler for a routine, expected outcome.
      if (!win) return

      copyStyles(document, win.document)
      win.document.title = 'Claro scribe'
      win.document.body.style.margin = '0'

      const host = win.document.createElement('div')
      win.document.body.appendChild(host)

      // Closing from the window's own chrome (or the browser reclaiming it)
      // has to reach React, or the next open() no-ops against a dead ref.
      win.addEventListener('pagehide', teardown)

      windowRef.current = win
      setContainer(host)
    },
    [teardown],
  )

  /**
   * MUST be called from a user gesture. Measured 2026-08-19: a document PiP
   * window throws
   *
   *   NotAllowedError: resizeTo() requires user activation in document
   *   picture-in-picture
   *
   * without one. Calling this from an effect — say, when a note arrives — is
   * therefore guaranteed to fail, which is exactly how the window shipped
   * stuck at its opening size. The failure is LOGGED rather than swallowed:
   * an empty catch here is what let that reach a clinician.
   */
  const resize = useCallback((size: PopoutSize) => {
    try {
      windowRef.current?.resizeTo(size.width, size.height)
    } catch (e) {
      console.warn('[popout] resize refused — needs a user gesture', e)
    }
  }, [])

  // Never outlive the mounting component: an orphaned window would keep
  // showing a React tree that no longer updates.
  useEffect(() => close, [close])

  return {
    container,
    isOpen: container !== null,
    supported,
    open,
    close,
    resize,
  }
}
